import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export default function Privacy() {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen pt-20 bg-white dark:bg-slate-900">
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950/10" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors duration-200 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">{t.privacyPage.backToHome}</span>
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.privacyPage.title}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            {t.privacyPage.subtitle}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="space-y-12">
            {/* Section 1: Controller */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">1.</span>
                {t.privacyPage.sections.controller}
              </h2>
              <div className="space-y-2 text-slate-700 dark:text-slate-300 leading-relaxed">
                <p className="font-medium">Avalon X Media GmbH</p>
                <p>Schozacherstr. 40</p>
                <p>70437 Stuttgart</p>
                <p>Germany</p>
                <div className="pt-4 space-y-1">
                  <p>
                    <span className="text-slate-500 dark:text-slate-500">{t.contactPage.email}:</span>{' '}
                    <a href="mailto:service@avalonxmedia.com" className="text-emerald-600 dark:text-emerald-400 hover:underline">
                      service@avalonxmedia.com
                    </a>
                  </p>
                  <p>
                    <span className="text-slate-500 dark:text-slate-500">{t.contactPage.phone}:</span>{' '}
                    <a href="tel:+491723541325" className="text-emerald-600 dark:text-emerald-400 hover:underline">
                      +49 172 3541 325
                    </a>
                  </p>
                  <p className="pt-2">{t.imprintPage.managingDirector}: Mr. Denys Tarasenko</p>
                </div>
              </div>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 2: General Information */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">2.</span>
                {t.privacyPage.sections.generalInfo}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {t.privacyPage.sections.generalInfoText}
              </p>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 3: Purposes and Legal Basis */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">3.</span>
                {t.privacyPage.sections.purposes}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                {t.privacyPage.sections.purposesIntro}
              </p>
              <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                {t.privacyPage.sections.purposesList.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 4: Collection and Storage */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">4.</span>
                {t.privacyPage.sections.collection}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                {t.privacyPage.sections.collectionIntro}
              </p>
              <ul className="grid grid-cols-2 gap-2 text-slate-700 dark:text-slate-300">
                {t.privacyPage.sections.collectionList.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 5: Disclosure to Third Parties */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">5.</span>
                {t.privacyPage.sections.disclosure}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                {t.privacyPage.sections.disclosureIntro}
              </p>
              <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                {t.privacyPage.sections.disclosureList.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 6: Rights of Data Subjects */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">6.</span>
                {t.privacyPage.sections.rights}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                {t.privacyPage.sections.rightsIntro}
              </p>
              <ul className="grid md:grid-cols-2 gap-2 text-slate-700 dark:text-slate-300">
                {t.privacyPage.sections.rightsList.map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 7: Data Retention */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">7.</span>
                {t.privacyPage.sections.retention}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {t.privacyPage.sections.retentionText}
              </p>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 8: Data Security */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">8.</span>
                {t.privacyPage.sections.security}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {t.privacyPage.sections.securityText}
              </p>
            </div>

            <div className="h-px bg-slate-200 dark:bg-slate-800" />

            {/* Section 9: Updates and Changes */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-baseline gap-3">
                <span className="text-emerald-600 dark:text-emerald-400">9.</span>
                {t.privacyPage.sections.updates}
              </h2>
              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                {t.privacyPage.sections.updatesText}{' '}
                <a href="https://www.avalonxmedia.com" className="text-emerald-600 dark:text-emerald-400 hover:underline">
                  www.avalonxmedia.com
                </a>
              </p>
            </div>
          </div>

          {/* Last Updated & Back to Home */}
          <div className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
              {t.privacyPage.lastUpdated}
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors duration-200 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.privacyPage.backToHome}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
